import React from 'react';
import { InboxOutlined, SearchOutlined } from '@ant-design/icons';
import { Empty, Modal, UploadFile } from 'antd';
import Dragger from 'antd/es/upload/Dragger';
import { TFunction } from 'i18next';
import UploadingComponent from 'components/UploadingComponent/UploadingComponent';
import { RcFile } from 'antd/es/upload';
import api from 'api';
import { useNavigate } from 'react-router';
import { useAppDispatch, useAppSelector } from 'store/hook';
import {
  setReportDocumentData,
  setReportDocumentRawData,
  setReportDocumentID,
  setReportDocumentRoomID,
  setReportDocumentName,
  setReportSegment,
  setIsReportDocumentHasNoToken,
} from 'store/slices/researchSlice';
import prepareDocumentInfo from 'components/DocumentsResearch/helpers/prepareDocumentInfo';
import { availableIconFormat, documnentIcons } from 'helpers/documentIcons';
import classes from './ResearchUploadModal.module.scss';

import { setViewType } from 'store/slices/dataDocumentsSlice';
import { setDataRoom } from 'store/slices/dataRoomSlice';
import { getTheme } from 'helpers/getTheme';

type ResearchUploadModalProps = {
  isOpen: boolean;
  onCancel: () => void;
  t: TFunction;
};

const iconRender = (file: UploadFile) => {
  const formatNDA = file?.name?.split('.')?.reverse()[0];
  const icon = availableIconFormat.includes(formatNDA) ? formatNDA : 'default';
  return documnentIcons[icon as keyof typeof documnentIcons];
};

const ResearchUploadModal = ({
  isOpen,
  onCancel,
  t,
}: ResearchUploadModalProps) => {
  const [isLoading, setIsLoading] = React.useState(false);
  const navigate = useNavigate();
  const [uploadFile, setUploadFile] = React.useState<UploadFile | null>(null);
  const dispatch = useAppDispatch();

  const { userData } = useAppSelector((state) => state.userData);
  const { messageApi } = useAppSelector((state) => state.windowState);

  const beforeUpload = (file: UploadFile) => {
    const extension = file.name.match(/\.(\w+)$/)?.[1]?.toLowerCase();
    if (extension === 'exe' || extension === 'bin') {
      messageApi?.error(t('Documents.error.notSupported'));
      return false;
    }
    setUploadFile(file);
    return false;
  };

  const onRemove = (file: UploadFile) => {
    setUploadFile(null);
  };

  const handleOk = async () => {
    if (!uploadFile || isLoading) return;
    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('document', uploadFile as RcFile);
      const response = await api.researchDocument(formData);
      const infoForTables = prepareDocumentInfo(response.data, t, userData!);
      dispatch(setReportDocumentRawData(response.data));
      dispatch(setReportDocumentData(infoForTables));
      dispatch(setReportDocumentRoomID(response.data?.previous.room_id || ''));
      dispatch(setReportDocumentID(response.data?.previous.document_id));
      dispatch(setReportDocumentName(response.data?.previous.name));
      dispatch(setIsReportDocumentHasNoToken(false));
    } catch (error) {
      console.log(error);
      dispatch(setIsReportDocumentHasNoToken(true));
      dispatch(setReportDocumentRawData(null as any));
      dispatch(setReportDocumentRoomID(''));
      dispatch(setReportDocumentID(''));
      dispatch(setReportDocumentName(uploadFile?.name || ''));
    } finally {
      onCancel();
      navigate('/report');
      dispatch(setViewType('Report'));
      dispatch(setReportSegment('info'));
      dispatch(setDataRoom(null))
      setIsLoading(false);
    }
  };

  return (
    <Modal
      width={'60%'}
      open={isOpen}
      centered
      title={t('Report.upload.title')}
      okText={t('Report.upload.button')}
      okButtonProps={{
        disabled: !uploadFile || isLoading,
        loading: isLoading,
        icon: <SearchOutlined />,
      }}
      onCancel={onCancel}
      className={getTheme()}
      onOk={handleOk}
    >
      <Dragger
        name='file'
        multiple={false}
        iconRender={iconRender}
        listType='picture'
        className={classes.dragger}
        beforeUpload={beforeUpload}
        onRemove={onRemove}
        fileList={uploadFile ? [uploadFile] : []}
        accept={'.docx, .pdf, .pptx, .xlsx'}
        style={{ borderRadius: 4 }}
      >
        <Empty
          rootClassName={classes.emptyElement}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={<>
            <p>{t('Report.upload.dropText')}</p>
            <p>{t('Documents.modals.upload.format')}</p>
          </>}
        />
      </Dragger>

      {isLoading && <UploadingComponent />}
    </Modal>
  );
};

export default ResearchUploadModal;
